import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Plus, AlertCircle, DollarSign, CheckCircle } from 'lucide-react';
import { ServiceAddon, getServiceAddons } from '../../lib/services';

interface SelectServiceAddonsModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  onSelect: (addons: ServiceAddon[]) => void;
  selectedAddons?: ServiceAddon[];
}

export default function SelectServiceAddonsModal({
  isOpen,
  onClose,
  serviceId,
  onSelect,
  selectedAddons = []
}: SelectServiceAddonsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set(selectedAddons.map(a => a.id)));

  useEffect(() => {
    console.log('🔍 SelectServiceAddonsModal useEffect:', { isOpen, serviceId });
    if (isOpen) {
      console.log('✅ Opening SelectServiceAddonsModal for service:', serviceId);
      setIsAnimating(true);
      loadAddons();
    }
  }, [isOpen, serviceId]);

  const loadAddons = async () => {
    if (!serviceId) {
      console.error('❌ SelectServiceAddonsModal: No serviceId provided');
      setError('No service ID provided');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      console.log('📦 Loading add-ons for serviceId:', serviceId);
      const data = await getServiceAddons(serviceId);
      console.log('✅ Loaded add-ons:', data);
      setAddons(data);
    } catch (err) {
      console.error('❌ Error loading add-ons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSelect = (addon: ServiceAddon) => {
    const newSelected = new Set(selectedAddonIds);
    if (newSelected.has(addon.id)) {
      newSelected.delete(addon.id);
    } else {
      newSelected.add(addon.id);
    }
    setSelectedAddonIds(newSelected);
  };

  const handleSave = () => {
    const selectedAddons = addons.filter(addon => selectedAddonIds.has(addon.id));
    onSelect(selectedAddons);
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  // Modal content to be rendered via portal
  const modalContent = (
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100500]`}
      onClick={onClose}
      style={{
        zIndex: 100500,
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div 
        className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top, 0px)',
          backgroundColor: '#000', // Black behind the notch area to match header
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col bg-white">
          {/* Header */}
          <div className="bg-black border-b border-gray-800">
            <div className="flex items-center justify-between px-6 py-4">
              <h2 className="text-xl font-semibold text-white">
                Select Add-ons
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50">
            <div className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {loading ? (
                <div className="flex flex-col items-center justify-center h-32">
                  <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-3"></div>
                  <div className="text-sm text-gray-600">Loading add-ons...</div>
                </div>
              ) : addons.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Plus className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Add-ons Available</h3>
                  <p className="text-sm text-gray-600">This service doesn't have any additional options</p>
                </div>
            ) : (
                <div className="space-y-3">
                  {addons.map(addon => (
                    <div
                      key={addon.id}
                      className={`bg-white border rounded-lg transition-colors cursor-pointer ${
                        selectedAddonIds.has(addon.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleSelect(addon)}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className={`flex-shrink-0 h-8 w-8 rounded-lg flex items-center justify-center ${
                              selectedAddonIds.has(addon.id)
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-gray-100 text-gray-400'
                            }`}>
                              {selectedAddonIds.has(addon.id) ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <Plus className="h-4 w-4" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="text-base font-medium text-gray-900 mb-1">
                                {addon.name}
                              </div>
                              {addon.description && (
                                <div className="text-sm text-gray-600 mb-2 line-clamp-2">
                                  {addon.description}
                                </div>
                              )}
                              <div className="flex items-center text-sm text-gray-500">
                                <Clock className="h-4 w-4 mr-1" />
                                {addon.duration_minutes >= 1440 
                                  ? `${Math.round(addon.duration_minutes / 1440)} days`
                                  : addon.duration_minutes >= 60
                                    ? `${Math.floor(addon.duration_minutes / 60)}h${addon.duration_minutes % 60 > 0 ? ` ${addon.duration_minutes % 60}m` : ''}`
                                    : `${addon.duration_minutes} min`
                                }
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center ml-3">
                            <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                            <div className="text-base font-semibold text-gray-900">
                              {addon.price.toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="bg-white border-t border-gray-200 p-6" style={{ paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }}>
            <button
              onClick={handleSave}
              disabled={loading}
              className={`w-full px-4 py-3 text-white font-semibold rounded-lg transition-colors shadow-sm ${
                loading ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </div>
              ) : (
                `Add Selected Add-ons ${selectedAddonIds.size > 0 ? `(${selectedAddonIds.size})` : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal content via portal to document.body to avoid z-index issues
  return createPortal(modalContent, document.body);
} 